/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './CommentsPanel';

const CommentsPanelManager: ComponentManagerFn<'CommentsPanel'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'CommentsPanel'>(platform, 'CommentsPanel'),
    isChildAllowed: (page, item, childType) =>
      (
        [
          'CommentActionMenuItems',
          'TriggerOnComment',
          'TriggerOnMention',
        ] as ItemType[]
      ).includes(childType),
    type: 'CommentsPanel',
    name: 'Comments Panel',
    defaultValue,
    props: {
      placeholder: {
        label: 'Placeholder',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'A placeholder that will be displayed in the comment box before the user starts typing the comments.',
      },
      context: {
        label: 'Context',
        type: 'string',
        placeholder: 'e.g. InvoiceComments',
        helpText:
          'A unique context across your application. e.g. OrderComments, InvoiceComments etc.',
      },
      contextValue: {
        label: 'Context Value',
        type: 'expression',
        helpText: `A value within the above context to seperate out the comments.

You can provide a static text, e.g. "All", if you don't want to seperate the comments.

To have different comments per record or record type, you can provide an expression e.g. \`\${$.alias.invoiceId}\` or \`\${$.alias.invoiceType}\``,
        returnType: async () => 'string',
      },
      panelWidth: {
        label: 'Width',
        type: 'number',
        min: 740,
        max: 1200,
        step: 40,
      },
      enableRemoveAlways: {
        label: 'Enable Remove Always',
        type: 'boolean',
        helpText:
          "Enable this to enable remove action always. By default it'll be enabled for 5min.",
      },
    },
    getDisplayText: (item) => item.props.context,
    category: 'Data',
    canHaveChildren: () => false,
    noOverlay: true,
    layout: 'auto',
    icon: { iconName: 'comments' },
    description: `Comments component is used to write comments or reviews on any record by setting context and context value. Comments will be displayed in a Drawer. A Drawer is a side sheet containing supplementary content that are anchored to the left or right edge of the screen.

For instance, if you want the users to be able to comment on invoices. You can set the context to \`InvoiceComments\` and the context value to \`$.InvoiceStoreAlias.invoiceId\`

\`\`\`alert_warning
Note: The expression for context value must return a string value. If the invoiceId attribute in the above example is a number, enclose it within a backtick  to convert it to a string e.g. \`\${$.InvoiceStoreAlias.invoiceId}\`
\`\`\`
`,
  };
};

export default CommentsPanelManager;
